import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { MENU_ITEMS } from '../config/MenuConfig';

export const useSidebarState = () => {
  const location = useLocation();

  // Simplified state - only what's needed
  const [sidebarState, setSidebarState] = useState({
    activeItem: 'dashboard',
    expandedMenus: new Set(),
    modals: {
      createProject: false
    }
  });

  // Find which menu item matches the current path
  const findActiveMenuFromPath = (pathname) => {
    // Check for exact matches first (primary path + subitems)
    for (const item of MENU_ITEMS) {
      if (item.path === pathname) {
        return { menuId: item.id, hasSubItems: !!item.subItems?.length };
      }
      if (item.subItems) {
        for (const subItem of item.subItems) {
          if (subItem.path === pathname) {
            return { menuId: item.id, hasSubItems: true };
          }
        }
      }
    }

    // Then check for partial matches (startsWith) on primary path + additionalPaths
    for (const item of MENU_ITEMS) {
      if (pathname.startsWith(item.path) && item.path !== '/') {
        return { menuId: item.id, hasSubItems: !!item.subItems?.length };
      }
      if (item.additionalPaths) {
        for (const extra of item.additionalPaths) {
          if (pathname === extra || pathname.startsWith(extra + '/') || pathname.startsWith(extra + '?')) {
            return { menuId: item.id, hasSubItems: !!item.subItems?.length };
          }
        }
      }
      if (item.subItems) {
        for (const subItem of item.subItems) {
          if (pathname.startsWith(subItem.path) && subItem.path !== '/') {
            return { menuId: item.id, hasSubItems: true };
          }
        }
      }
    }

    return { menuId: null, hasSubItems: false };
  };

  // Update active item and expand menus based on current path
  useEffect(() => {
    const menuMatch = findActiveMenuFromPath(location.pathname);

    setSidebarState(prev => {
      const newExpandedMenus = new Set(prev.expandedMenus);

      // Set active menu item
      const activeItem = menuMatch.menuId || 'dashboard';

      // Auto-expand menu if it has subitems and matches current path
      if (menuMatch.hasSubItems) {
        newExpandedMenus.add(menuMatch.menuId);
      }

      return {
        ...prev,
        activeItem,
        expandedMenus: newExpandedMenus
      };
    });
  }, [location.pathname]);

  // Helper functions
  const updateState = (updates) => {
    setSidebarState(prev => ({ ...prev, ...updates }));
  };

  const toggleExpanded = (menuId) => {
    setSidebarState(prev => {
      const newExpandedMenus = new Set(prev.expandedMenus);
      if (newExpandedMenus.has(menuId)) {
        newExpandedMenus.delete(menuId);
      } else {
        newExpandedMenus.add(menuId);
      }
      return { ...prev, expandedMenus: newExpandedMenus };
    });
  };

  const openModal = (modalName) => {
    setSidebarState(prev => ({
      ...prev,
      modals: { ...prev.modals, [modalName]: true }
    }));
  };

  const closeModal = (modalName) => {
    setSidebarState(prev => ({
      ...prev,
      modals: { ...prev.modals, [modalName]: false }
    }));
  };

  return {
    sidebarState,
    updateState,
    toggleExpanded,
    openModal,
    closeModal
  };
};